/**
 * Turns a PascalCase, dotted `fieldPath` from the audit payload into a
 * human-readable label.
 *
 * Examples:
 *   `ClaimOwnerCapacity.MaxWeeklyCapacity`  -> "Max Weekly Capacity"
 *   `Locations.[<guid>].State`              -> "Location › State"
 *   `Locations.[abc-123].Address.City`      -> "Location › Address › City"
 *
 * Collection index tokens (`[<guid>]`, `[0]`, `[abc-123]`) are dropped, and
 * the owning collection segment immediately before an index is singularized
 * so `Locations.[..]` reads as "Location".
 *
 * Exception: a *leaf* token belonging to a literal-key collection is kept
 * verbatim, so `Metadata.[Region]` reads as "Metadata › Region". Its identity
 * is a human-readable key that names *which* entry changed — not a GUID that
 * gets resolved to a name elsewhere (that resolution happens in the value cell,
 * see `membershipValues`).
 */

import type { MemberNameResolver } from './membershipValues';

// A bracketed collection index: `[<guid>]`, `[0]`, `[abc-123]`, `[<id>]`, etc.
const INDEX_TOKEN = /^\[.*\]$/;

// Collections whose bracket identity is a literal, human-readable key rather
// than a GUID reference — e.g. `Metadata.[Region]`. A leaf key of one of these
// is kept in the label; there is nothing to resolve and dropping it would lose
// *which* entry changed.
const LITERAL_KEY_COLLECTIONS = new Set<string>(['Metadata']);

// Friendly labels for path segments whose PascalCase name leaks storage-type or
// internal jargon into the UI. The attribute-value collections differ only by
// their SQL storage type (Varchar/Int/Float/Bool), which is meaningless to a
// reader, so they all collapse to "Attribute Value".
//
// Written in ALREADY-SINGULAR form: an override still passes through
// `singularize` when an index token follows it, and a singular value is a no-op
// there. `SelectedCertificationsAndLicenses` is overridden for exactly that
// reason — "Licenses" and "Addresses" both end in "ses" but singularize
// differently ("License" vs "Address"), which no suffix rule can separate.
const SEGMENT_LABEL_OVERRIDES: Record<string, string> = {
  SelectedAttributeValuesVarchars: 'Attribute Value',
  SelectedAttributeValuesInts: 'Attribute Value',
  SelectedAttributeValuesFloats: 'Attribute Value',
  SelectedAttributeValuesBools: 'Attribute Value',
  SelectedCertificationsAndLicenses: 'Selected Certification And License',
  // The C# property is misspelled `ExlcusionZones`, and the audit field path is written from a
  // hardcoded literal of that name — so every stored record carries the misspelling and always
  // will. This override is permanent, not a migration stopgap: renaming the property would fix
  // only records written afterwards while breaking 8 consuming repos at compile time. The wire
  // format was never wrong (`[JsonProperty("exclusionZones")]`); only the identifier is.
  ExlcusionZones: 'Exclusion Zones',
};

function splitPascalCase(segment: string): string {
  return (
    segment
      // insert a space before a capital that follows a lowercase/digit
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      // split runs of capitals followed by a capital+lowercase (e.g. "MaxWeekly" ok, "IDValue")
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      .trim()
  );
}

function singularize(word: string): string {
  if (/ies$/i.test(word)) {
    return word.replace(/ies$/i, 'y');
  }
  if (/ses$/i.test(word)) {
    return word.replace(/es$/i, '');
  }
  if (/s$/i.test(word) && !/ss$/i.test(word)) {
    return word.replace(/s$/i, '');
  }
  return word;
}

export function formatFieldPath(
  fieldPath: string,
  resolveName?: MemberNameResolver
): string {
  if (!fieldPath) {
    return '';
  }

  const rawSegments = fieldPath.split('.');
  const labelParts: string[] = [];

  for (let i = 0; i < rawSegments.length; i++) {
    const segment = rawSegments[i];

    // Drop index tokens; singularize the preceding collection label already
    // pushed (e.g. "Locations" -> "Location"). A leaf token of a literal-key
    // collection (e.g. a metadata key) is instead kept verbatim — it names
    // which entry changed and is not a GUID resolved elsewhere.
    if (INDEX_TOKEN.test(segment)) {
      const last = labelParts.length - 1;
      if (last >= 0) {
        labelParts[last] = singularize(labelParts[last]);
      }

      const owningCollection = i > 0 ? rawSegments[i - 1] : '';
      const isLeaf = i === rawSegments.length - 1;
      if (isLeaf && LITERAL_KEY_COLLECTIONS.has(owningCollection)) {
        labelParts.push(segment.slice(1, -1));
        continue;
      }

      // A NON-leaf index owns the field being changed, so name it: an update to
      // one member of a collection otherwise reads "Location › Street" and never
      // says which location. A leaf index is deliberately skipped —
      // that is a membership add/remove, whose identity `formatChangeCells`
      // already renders in the value cell ("Home Office (deleted)").
      //
      // An unresolvable member (typically one since removed from the resource)
      // keeps the bare "Location › Street". The GUID is NEVER substituted in:
      // a raw GUID on screen is worse than no identity at all.
      if (!isLeaf && last >= 0 && resolveName) {
        const memberName = resolveName(owningCollection, segment.slice(1, -1));
        if (memberName) {
          labelParts[last] = `${labelParts[last]} (${memberName})`;
        }
      }

      continue;
    }

    const pretty = SEGMENT_LABEL_OVERRIDES[segment] ?? splitPascalCase(segment);
    if (pretty) {
      labelParts.push(pretty);
    }
  }

  return labelParts.join(' › ');
}
